<script lang="ts">
  import { crossfade, scale } from 'svelte/transition';
  import { quintOut } from 'svelte/easing';
  import BarChartIcon from '~icons/feather/bar-chart';
  import type { WordSort } from '../types';
  import ActionButton from './ActionButton.svelte';

  type Option = { value: WordSort; label: string };

  interface Props {
    value: WordSort;
    options: Option[];
  }

  let { value = $bindable(), options }: Props = $props();

  let isOpen = $state(false);
  let container: HTMLElement;

  const [send, receive] = crossfade({
    duration: 200,
    easing: quintOut,
    fallback: (node) => scale(node, { start: 0.9, duration: 180, easing: quintOut }),
  });

  const currentLabel = $derived(options.find((o) => o.value === value)?.label ?? '');

  function select(v: WordSort) {
    value = v;
    isOpen = false;
  }

  function handleWindowClick(e: MouseEvent) {
    if (isOpen && container && !container.contains(e.target as Node)) {
      isOpen = false;
    }
  }

  function handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') isOpen = false;
  }
</script>

<svelte:window onclick={handleWindowClick} onkeydown={handleKeydown} />

<div class="sort-dropdown" bind:this={container}>
  <ActionButton onClick={() => { (isOpen = !isOpen) }}>
    <span class="icon"><BarChartIcon /></span>
      <span class="label" out:send={{ key: 'label' }} in:receive={{ key: 'label' }}>
        {currentLabel}
      </span>
  </ActionButton>

  {#if isOpen}
    <div class="panel" transition:scale={{ start: 0.85, duration: 200, easing: quintOut }}>
      <span>sort</span>
      {#each options as option (option.value)}
        <button
          class="option"
          class:active={option.value === value}
          onclick={() => select(option.value)}
        >
          {#if option.value === value}
            <span in:receive={{ key: 'label' }} out:send={{ key: 'label' }}>
              {option.label}
            </span>
          {:else}
            {option.label}
          {/if}
        </button>
      {/each}
    </div>
  {/if}
</div>

<style lang="scss">
  @use 'sass:color';

  $color: hsl(213, 56%, 71%);
  $dark-color: #2f3640;

  .sort-dropdown {
    position: relative;
    width: 8em;
    :global(button) {
      width: 8em;
    }
  }

  .icon {
    display: flex;
    align-items: center;
    transform: rotate(-90deg);
  }

  .label {
    display: inline-block;
  }

  .panel {
    position: absolute;
    bottom: 0;
    right: 0;
    transform-origin: bottom right;
    background-color: color.scale($color, $lightness: 80%);
    border: 2px solid #2f3640;
    border-radius: 22px;
    overflow: hidden;
    box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.15);
    z-index: 100;
    min-width: 100%;

    :global(html.dark-mode) & {
      background-color: $dark-color;
      border-color: white;
      box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.5);
    }
  }

  .option {
    display: block;
    width: 100%;
    padding: 10px 16px;
    text-align: left;
    background: none;
    border: none;
    font-weight: bold;
    font-size: inherit;
    text-transform: uppercase;
    color: #2f3640;
    cursor: pointer;
    transition: background-color 0.1s;
    white-space: nowrap;

    &:hover {
      background-color: color.scale($color, $lightness: 50%);
    }

    &.active {
      background-color: color.scale($color, $lightness: 60%);
    }

    :global(html.dark-mode) & {
      color: white;

      &:hover {
        background-color: color.scale($dark-color, $lightness: 15%);
      }

      &.active {
        background-color: color.scale($dark-color, $lightness: 10%);
      }
    }
  }
</style>
